import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect for navbar
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "What is a VPN and why do I need it?",
      answer: "A VPN (Virtual Private Network) creates a secure, encrypted connection between your device and the internet. It protects your online privacy, secures your data from hackers, and allows you to browse anonymously. With increasing cyber threats and privacy concerns, a VPN is essential for protecting your digital identity."
    },
    {
      question: "How does EL CALDEN VPN ensure my privacy?",
      answer: "We implement military-grade AES-256 encryption, maintain a strict no-log policy, and operate under privacy-friendly jurisdiction. We never track, collect, or share your browsing data. Your online activities remain completely private and anonymous."
    },
    {
      question: "What platforms does your VPN support?",
      answer: "Our VPN application is available for Windows, macOS, Linux, iOS, and Android. You can protect all your devices with a single account and enjoy seamless security across all platforms."
    },
    {
      question: "Will the VPN slow down my internet connection?",
      answer: "Our optimized server network and advanced protocols ensure minimal speed impact. While encryption adds slight overhead, most users experience negligible difference in their browsing speed. We continuously optimize our infrastructure for maximum performance."
    },
    {
      question: "What is the Kill Switch feature?",
      answer: "The Kill Switch is a critical security feature that automatically disconnects your internet if the VPN connection drops unexpectedly. This prevents your real IP address and data from being exposed, ensuring continuous protection even during connection interruptions."
    },
    {
      question: "How do I get started with EL CALDEN VPN?",
      answer: "Getting started is simple: download our application for your device, install it, and launch the app. Connect with one click to our secure servers and enjoy protected browsing immediately. Our intuitive interface makes VPN protection accessible to everyone."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="https://public.readdy.ai/ai/img_res/ea5be7b6-4733-4113-81cf-baaff424d1f3.png" alt="EL CALDEN S.R.L." className="h-12 w-12 object-contain" />
              <h1 className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>EL CALDEN S.R.L.</h1>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-300'}`}>Home</a>
              <a href="#about" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-300'}`}>About</a>
              <a href="#vpn-service" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-300'}`}>VPN Service</a>
              <a href="#features" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-300'}`}>Features</a>
              <a href="#contact" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-300'}`}>Contact</a>
              <a href="#faq" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-300'}`}>FAQ</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/ba632c9a31949ba1632955158a5c2e5b.jpg" 
            alt="VPN Security Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 w-full max-w-4xl mx-auto">
          <h2 className="text-6xl md:text-7xl font-bold text-white mb-4" style={{ textShadow: '0 0 40px rgba(0,217,255,0.3)' }}>
            EL CALDEN S.R.L.
          </h2>
          <p className="text-4xl md:text-5xl font-serif italic text-white mb-8 bg-gradient-to-r from-cyan-300 to-white bg-clip-text text-transparent">
            Secure VPN Solutions
          </p>
          <p className="text-lg text-white/80 mb-12 max-w-2xl mx-auto leading-relaxed">
            Professional VPN Application for Complete Online Privacy
          </p>
          <a 
            href="#vpn-service" 
            className="inline-flex items-center px-10 py-4 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-full transition-all duration-300 shadow-lg hover:shadow-cyan-500/50 whitespace-nowrap cursor-pointer"
          >
            Get Started
            <i className="ri-arrow-right-line ml-2 text-xl"></i>
          </a>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-2">
              <div className="inline-block px-4 py-2 bg-slate-200 rounded-full mb-6">
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">About Us</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 leading-tight">
                Protecting Your<br />Digital Freedom
              </h3>
              <p className="text-slate-600 text-base leading-relaxed">
                EL CALDEN S.R.L. is dedicated to providing enterprise-grade VPN solutions that protect your online privacy and security. Based in Villa Mercedes, Argentina, we develop cutting-edge VPN applications that ensure your digital activities remain private and secure. Our mission is to make online privacy accessible to everyone through innovative technology and unwavering commitment to user security.
              </p>
            </div>
            <div className="md:col-span-3">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%203D%20illustration%20of%20encrypted%20data%20flow%20with%20shield%20icons%20and%20lock%20symbols%2C%20teal%20and%20cyan%20gradient%20background%2C%20floating%20UI%20elements%20showing%20VPN%20connection%20status%20and%20security%20indicators%2C%20abstract%20representation%20of%20digital%20protection%20with%20data%20packets%20moving%20across%20secure%20network%2C%20professional%20tech%20visualization&width=800&height=600&seq=about-visual-001&orientation=landscape" 
                  alt="VPN Security Visualization" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Service Section */}
      <section id="vpn-service" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-shield-check-line text-2xl text-cyan-500"></i>
              </div>
              <span className="text-sm font-semibold text-cyan-600 uppercase tracking-wide">Our Service</span>
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900">
              Enterprise-Grade VPN Protection
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900 to-cyan-500"></div>
              <div className="relative p-10 text-center h-full flex flex-col justify-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <i className="ri-lock-password-line text-6xl text-white" style={{ filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.5))' }}></i>
                </div>
                <h4 className="text-2xl font-bold text-white mb-4">Military-Grade Encryption</h4>
                <p className="text-white/90 text-sm leading-relaxed">
                  Protect your data with AES-256 encryption, the same standard used by governments and security experts worldwide to safeguard classified information.
                </p>
              </div>
            </div>

            <div className="group relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-700 to-pink-500"></div>
              <div className="relative p-10 text-center h-full flex flex-col justify-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <i className="ri-global-line text-6xl text-white" style={{ filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.5))' }}></i>
                </div>
                <h4 className="text-2xl font-bold text-white mb-4">Global Network</h4>
                <p className="text-white/90 text-sm leading-relaxed">
                  Access our worldwide server network for optimal performance and reliability. Connect to any location and enjoy unrestricted internet access.
                </p>
              </div>
            </div>

            <div className="group relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-600 to-emerald-500"></div>
              <div className="relative p-10 text-center h-full flex flex-col justify-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <i className="ri-speed-line text-6xl text-white" style={{ filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.5))' }}></i>
                </div>
                <h4 className="text-2xl font-bold text-white mb-4">Lightning Fast Speed</h4>
                <p className="text-white/90 text-sm leading-relaxed">
                  Experience blazing-fast connection speeds with our optimized infrastructure. Stream, download, and browse without compromise.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-slate-800 rounded-2xl p-8 flex items-start space-x-6">
              <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-keyhole-line text-5xl text-cyan-400"></i>
              </div>
              <div>
                <h4 className="text-2xl font-bold text-white mb-2">Military-Grade Encryption</h4>
                <p className="text-cyan-400 text-sm font-semibold mb-3">AES-256 Encryption Protocol</p>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Your data is protected with the highest level of encryption available. AES-256 is virtually unbreakable and ensures that your online activities, personal information, and sensitive data remain completely secure from any unauthorized access.
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-slate-800 rounded-2xl p-6 flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-4xl text-cyan-400"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">No-Log Policy</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    We never track, store, or share your browsing history. Your privacy is our priority.
                  </p>
                </div>
              </div>

              <div className="bg-slate-800 rounded-2xl p-6 flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <i className="ri-flashlight-line text-4xl text-cyan-400"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">Ultra-Fast Servers</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Optimized server infrastructure ensures minimal latency and maximum speed.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-2xl p-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-eye-off-line text-4xl text-cyan-400"></i>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">No-Log Policy</h4>
                <p className="text-slate-400 text-sm">Complete privacy with zero activity logging</p>
              </div>
              <div className="text-center border-l border-r border-slate-700">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shut-down-line text-4xl text-cyan-400"></i>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Kill Switch Protection</h4>
                <p className="text-slate-400 text-sm">Automatic disconnect if VPN drops</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-device-line text-4xl text-cyan-400"></i>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Multi-Platform Support</h4>
                <p className="text-slate-400 text-sm">Available on all major devices and OS</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-white to-cyan-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-2xl p-16">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-slate-900 mb-4">Get In Touch</h3>
              <p className="text-slate-600">We're here to help with your VPN needs</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 divide-x divide-slate-200">
              <div className="text-center group cursor-pointer transition-all duration-300 hover:bg-cyan-50 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110">
                  <i className="ri-mail-line text-5xl text-cyan-600"></i>
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Email</h4>
                <a href="mailto:support@elcaldensrl.com" className="text-cyan-600 hover:text-cyan-700 text-sm whitespace-nowrap">
                  support@elcaldensrl.com
                </a>
              </div>

              <div className="text-center group cursor-pointer transition-all duration-300 hover:bg-cyan-50 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110">
                  <i className="ri-phone-line text-5xl text-cyan-600"></i>
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Phone</h4>
                <a href="tel:+17754029130" className="text-cyan-600 hover:text-cyan-700 text-sm whitespace-nowrap">
                  +1 7754029130
                </a>
              </div>

              <div className="text-center group cursor-pointer transition-all duration-300 hover:bg-cyan-50 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110">
                  <i className="ri-map-pin-line text-5xl text-cyan-600"></i>
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Address</h4>
                <div className="text-slate-600 text-sm">
                  <p>SAN MARTIN 887</p>
                  <p>Villa Mercedes, D5732ALE</p>
                  <p>Argentina</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-100 rounded-full mb-4">
              <i className="ri-question-line text-blue-600"></i>
              <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">FAQ</span>
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h3>
            <p className="text-slate-600">Everything you need to know about our VPN service</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer"
                onClick={() => toggleFaq(index)}
              >
                <div className="flex items-center justify-between p-6">
                  <h4 className="text-lg font-bold text-slate-900 pr-8">{faq.question}</h4>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`text-2xl text-cyan-600 transition-transform duration-300 ${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </div>
                </div>
                <div 
                  className={`transition-all duration-300 overflow-hidden ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="px-6 pb-6 pt-0 border-t border-slate-100">
                    <p className="text-slate-600 text-sm leading-relaxed mt-4">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
        
        <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
          <div className="grid md:grid-cols-10 gap-12 mb-12">
            <div className="md:col-span-3">
              <div className="flex items-center space-x-2 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/ea5be7b6-4733-4113-81cf-baaff424d1f3.png" alt="EL CALDEN S.R.L." className="h-10 w-10 object-contain" />
                <span className="text-xl font-bold">EL CALDEN S.R.L.</span>
              </div>
              <p className="text-slate-400 italic text-sm mb-6">Secure VPN Solutions</p>
              <p className="text-slate-500 text-sm leading-relaxed">
                Providing enterprise-grade VPN solutions to protect your online privacy and security. Your trusted partner in digital freedom.
              </p>
            </div>

            <div className="md:col-span-2">
              <h5 className="text-white font-bold mb-4">Product</h5>
              <ul className="space-y-3">
                <li><a href="#vpn-service" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors whitespace-nowrap cursor-pointer">VPN Features</a></li>
                <li><a href="#features" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors whitespace-nowrap cursor-pointer">Security</a></li>
                <li><a href="#vpn-service" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors whitespace-nowrap cursor-pointer">Platforms</a></li>
                <li><a href="#vpn-service" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors whitespace-nowrap cursor-pointer">Download</a></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <h5 className="text-white font-bold mb-4">Company</h5>
              <ul className="space-y-3">
                <li><a href="#about" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors whitespace-nowrap cursor-pointer">About Us</a></li>
                <li><a href="#contact" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors whitespace-nowrap cursor-pointer">Contact</a></li>
                <li><a href="#" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors whitespace-nowrap cursor-pointer">Privacy Policy</a></li>
                <li><a href="#" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors whitespace-nowrap cursor-pointer">Terms of Service</a></li>
              </ul>
            </div>

            <div className="md:col-span-3">
              <h5 className="text-white font-bold mb-4">Support</h5>
              <ul className="space-y-3">
                <li><a href="#faq" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors whitespace-nowrap cursor-pointer">FAQ</a></li>
                <li><a href="#contact" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors whitespace-nowrap cursor-pointer">Help Center</a></li>
                <li><a href="mailto:support@elcaldensrl.com" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors whitespace-nowrap cursor-pointer">Email Support</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-slate-500 text-xs">© 2025 EL CALDEN S.R.L. All rights reserved</p>
            <div className="flex items-center space-x-4">
              <a href="#" className="w-10 h-10 flex items-center justify-center border border-slate-700 rounded-lg hover:border-cyan-400 hover:text-cyan-400 transition-colors cursor-pointer">
                <i className="ri-linkedin-line text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 flex items-center justify-center border border-slate-700 rounded-lg hover:border-cyan-400 hover:text-cyan-400 transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 flex items-center justify-center border border-slate-700 rounded-lg hover:border-cyan-400 hover:text-cyan-400 transition-colors cursor-pointer">
                <i className="ri-github-line text-lg"></i>
              </a>
            </div>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-cyan-400 text-xs transition-colors whitespace-nowrap cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
